import { Component, Input, Output, ViewEncapsulation, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { OverlayService } from '../../../../services/overlayService';
import { AuthService } from '../../../../services/auth.service';
import { EMAIL_REGEX } from '../../../../models';
import { SimpleUser } from '../../../../interfaces';

export interface SelectedInvitersChange {
  invalid?: boolean;
  value: string[];
  selectedData?: any;
  componentId?: any;
  removedData?: any;
  newData?: any;
}

@Component({
  selector: 'mtm-inviter-dropdown',
  templateUrl: './mtm-inviter-dropdown.component.html',
  styleUrl: './mtm-inviter-dropdown.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MtmInviterDropdownComponent {
  @Input() data: any[] = [];
  @Input() iconSize: number = 32;
  @Input() internalUsersOnly: boolean = false;
  @Input() userInfo: SimpleUser[] = [];
  @Input() inputPlaceholder: string;
  @Input() hideModelPreview: boolean = false;
  @Input() componentId: string;
  @Input() forceLoadProfile: boolean = false;
  @Input() singleInput: boolean = false;
  @Input() asClassicDropdown: boolean = false;
  @Output() selectedInvitersChange = new EventEmitter<SelectedInvitersChange>();

  modelValue: any = [];
  @Input()
  get value() {
    return this.modelValue || [];
  }
  @Output() valueChange: EventEmitter<any> = new EventEmitter();
  set value(val) {
    this.modelValue = val;
    this.valueChange.emit(this.modelValue);
  }
  isOpen: boolean = false;
  emailValue: any = '';
  suggestedUsers: any[] = [];

  constructor(
    private readonly overlayService: OverlayService,
    private readonly authService: AuthService,
    private readonly elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    //check if the click was outside the component
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      // clear suggestions if clicking outside
      if (this.suggestedUsers.length) {
        this.suggestedUsers = [];
        this.emailValue = '';
      }
    }
  }

  suggestUsers(e: KeyboardEvent) {
    for (const user of this.data) {
      user.fullName = user.fullName.trim();
    }
    const target = e.target as HTMLInputElement;
    if (target.value) {
      this.suggestedUsers = this.data.filter(
        (data) =>
          ((data?.email ?? '').toLowerCase().includes(target.value.toLowerCase()) ||
            (data?.fullName ?? '').toLowerCase().includes(target.value.toLowerCase())) &&
          !this.value.find((user) => user.email === data.email) &&
          data.email !== this.authService.getAuthUser().email
      );
    } else {
      this.suggestedUsers = [];
    }
  }

  suggestedUserSelected(user: SimpleUser) {
    this.value = this.singleInput ? [user.email] : [...this.value, ...[user]];
    this.emailValue = !this.asClassicDropdown ? '' : user.email;
    this.suggestedUsers = [];
    this.modelValue = this.value;
    this.selectedInvitersChange.emit({ value: this.value, selectedData: user, componentId: this.componentId });
  }

  removeUser(user: SimpleUser) {
    this.value = this.value.filter((data) => {
      return data.email !== user.email;
    });
    this.modelValue = this.value;
    this.selectedInvitersChange.emit({ value: this.value, removedData: user, componentId: this.componentId });
  }

  selectEmail(email: string, doNotShowOverlay?: boolean) {
    const emailAlreadyAdded = this.value.find((usr) => usr.email === email);
    if (emailAlreadyAdded) {
      this.overlayService.showError('This email has already invited', 'Email Invited');
      return;
    }
    if (!this.validateEmail(email)) {
      if (this.asClassicDropdown) {
        this.modelValue = this.value;
        this.selectedInvitersChange.emit({
          value: this.value,
          newData: email,
          invalid: true,
          componentId: this.componentId,
        });
      }
      if (!doNotShowOverlay) {
        this.overlayService.showError('Please use a valid email address', 'Invalid Email');
      }
      return;
    }
    const anonimUser = {
      type: 'anonim',
      email: email,
    };
    this.value = this.singleInput ? [email] : [...this.value, ...[anonimUser]];
    if (!this.asClassicDropdown) {
      this.emailValue = '';
      this.suggestedUsers = [];
    }
    this.modelValue = this.value;
    this.selectedInvitersChange.emit({ value: this.value, newData: email, componentId: this.componentId });
  }

  validateEmail(email: string): boolean {
    return EMAIL_REGEX.test(String(email).toLowerCase());
  }

  getSimpleString(data: any) {
    if (!data) {
      return '';
    }

    if (typeof data === 'string') {
      return data;
    }
    return '';
  }
}
